// src/main.tsx
import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App'
import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals'
import * as Sentry from '@sentry/react'
import { AppErrorBoundary } from './components/error/AppErrorBoundary'
import { applyStoredAnalyticsConsent, trackEvent } from './utils/analytics'

// ── Sentry (unchanged from prior config) ───────────────────────
// NOTE: Sentry config is intentionally not modified in this drop.
// See pre-launch audit notes for the v8-API and dev-gate
// recommendations; defer those until the analytics + consent
// flow is settled so we don't trip Sentry events during the
// rollout itself.
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      new Sentry.BrowserTracing(),
    ],
    tracesSampleRate: 0.1,
  })
}

// ── Web Vitals → analytics (consent-gated) ─────────────────────
// `trackEvent` is a no-op until the user accepts the analytics
// category in the cookie banner. Once accepted, both this and
// any future event() calls flush through gtag.
function sendToAnalytics(metric: any) {
  trackEvent(metric.name, {
    value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
    metric_id: metric.id,
    metric_label: metric.id,
  })
}

getCLS(sendToAnalytics)
getFID(sendToAnalytics)
getFCP(sendToAnalytics)
getLCP(sendToAnalytics)
getTTFB(sendToAnalytics)

// Re-apply persisted cookie-consent state at boot. If the user
// previously accepted analytics on this device, the GA loader
// installs now. If they declined, this is a no-op.
applyStoredAnalyticsConsent()

// ── Service Worker ─────────────────────────────────────────────
// Build tag is now injected at build time via Vite's `define`
// (see vite.config.ts) so each deploy carries a unique fingerprint
// without us hand-editing this file. If you don't see a fresh tag
// in console after deploy, your SW is serving stale JS.
import { registerSW } from 'virtual:pwa-register'

declare const __APP_BUILD_ID__: string
const BUILD_ID =
  typeof __APP_BUILD_ID__ === 'string' && __APP_BUILD_ID__
    ? __APP_BUILD_ID__
    : 'dev-build'

if ('serviceWorker' in navigator) {
  // eslint-disable-next-line no-console
  console.log(`SW bootstrap (build: ${BUILD_ID})`)

  registerSW({
    immediate: true,
    onRegistered(registration) {
      // eslint-disable-next-line no-console
      console.log('SW registered:', registration)
    },
    onRegisterError(err) {
      // eslint-disable-next-line no-console
      console.log('SW registration failed:', err)
    },
    onNeedRefresh() {
      // autoUpdate + skipWaiting takes care of activation; nothing
      // to prompt the user about.
      // eslint-disable-next-line no-console
      console.log('SW: new content available, will activate now')
    },
    onOfflineReady() {
      // eslint-disable-next-line no-console
      console.log('SW: app ready to work offline')
    },
  })
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppErrorBoundary
      onError={(err, info) => {
        // Report to Sentry if available. Wrapping in try/catch so a
        // reporter failure never blocks the fallback render.
        try {
          if (import.meta.env.VITE_SENTRY_DSN) {
            Sentry.captureException(err, {
              extra: { componentStack: info.componentStack },
            })
          }
        } catch {
          /* ignore */
        }
      }}
    >
      <App />
    </AppErrorBoundary>
  </React.StrictMode>,
)
